import { BASE_AUTH_OFFICE_API } from '@/core/api-config';
import { TokenManager } from '@/core/auth/token-manager';
import { ApiErrorMiddleware } from '@/core/api-error-middleware';
import { useOperatorStore } from '@/core/stores/operator-store';
import {
  ManufacturerItem,
  ManufacturerResponse,
  CreateManufacturerRequest,
  UpdateManufacturerRequest,
} from '../types/manufacturer-types';

export class ManufacturerApi {
  constructor(
    private readonly baseUrl: string = BASE_AUTH_OFFICE_API,
    private readonly tokenManager: TokenManager
  ) {}

  private async getAuthHeadersWithOperator(): Promise<Record<string, string>> {
    const operatorId = useOperatorStore.getState().selectedOperator?.operatorId;

    if (!operatorId) {
      console.error('ManufacturerApi: No operator selected, preventing API call');
      throw new Error('No operator selected. Please select an operator first.');
    }

    const headers = await this.tokenManager.getAuthHeaders();

    return {
      'Content-Type': 'application/json',
      ...headers,
      'X-Operator-Id': operatorId.toString(),
    };
  }

  async getAll(): Promise<ManufacturerResponse> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/dcs/manufacturers`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Manufacturer API',
      '/dcs/manufacturers'
    );

    const data = await response.json();

    // Handle different response formats:
    // 1. Array: [...items]
    // 2. Object with 'rows': { rows: [...], availablePlaceholders: [...] }
    // 3. Object with 'value': { value: [...] }
    const isArray = Array.isArray(data);
    const items = isArray ? data : (data.rows || data.value || []);

    // Map placeholder field name if needed (API uses 'placeholder', we use 'key')
    const placeholders = (data.availablePlaceholders ?? []).map((p: { placeholder?: string; key?: string; description: string; example?: string; source?: string }) => ({
      key: p.placeholder || p.key || '',
      description: p.description || '',
      example: p.example,
      source: p.source,
    }));

    return {
      value: items,
      '@odata.count': isArray ? items.length : (data.count ?? items.length),
      availablePlaceholders: placeholders,
      exampleTemplates: data.exampleTemplates ?? [],
      finalCostExampleTemplates: data.finalCostExampleTemplates ?? [],
    };
  }

  async getById(id: number): Promise<ManufacturerItem> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/dcs/manufacturers/${id}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'GET',
        headers,
      },
      'Manufacturer API',
      `/dcs/manufacturers/${id}`
    );

    return response.json();
  }

  async create(data: CreateManufacturerRequest): Promise<ManufacturerItem> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/dcs/manufacturers`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      },
      'Manufacturer API',
      '/dcs/manufacturers'
    );

    const result = await response.json();

    // Check for API-level validation errors (HTTP 200 but isSuccess: false)
    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || 'Failed to create manufacturer');
    }

    return result;
  }

  async update(id: number, data: UpdateManufacturerRequest): Promise<ManufacturerItem> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/dcs/manufacturers/${id}`;

    const response = await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify({ ...data, id }),
      },
      'Manufacturer API',
      `/dcs/manufacturers/${id}`
    );

    const result = await response.json();

    // Check for API-level validation errors (HTTP 200 but isSuccess: false)
    if (result.isSuccess === false) {
      throw new Error(result.errorMessage || 'Failed to update manufacturer');
    }

    return result;
  }

  async delete(id: number): Promise<void> {
    const headers = await this.getAuthHeadersWithOperator();
    const url = `${this.baseUrl}/dcs/manufacturers/${id}`;

    await ApiErrorMiddleware.fetchWithErrorHandling(
      url,
      {
        method: 'DELETE',
        headers,
      },
      'Manufacturer API',
      `/dcs/manufacturers/${id}`
    );
  }
}
